import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { debounce } from 'lodash';
import { Individual, Organization } from 'utils';
import { adminGetIndividuals } from '../../../admin.individuals.api';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import {
    assignPodToIndividual,
    changePodOrganization,
    detachPodIndividual,
    transferPodToIndividual,
} from '../../pods/pods.api';
import { addFp1DeviceNote } from '../fp1.api';
import type { Fp1Device } from '../fp1.types';
import StripeLink from '../../pods/components/StripeLink';

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function stripeChipColor(
    raw: string | null | undefined,
): 'success' | 'error' | 'default' | 'warning' {
    const s = raw?.trim().toLowerCase() || '';
    if (s === 'active') return 'success';
    if (s === 'past_due' || s === 'unpaid') return 'error';
    if (s === 'canceled' || s === 'cancelled') return 'default';
    return 'default';
}

interface Props {
    device: Fp1Device;
    onChanged: () => void;
}

const Fp1HierarchyPodAdminPanel: React.FC<Props> = ({ device, onChanged }) => {
    const podUuid = device.pod_uuid?.trim() || null;
    const [orgDialogOpen, setOrgDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [individuals, setIndividuals] = useState<Individual[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);
    const [isTransfer, setIsTransfer] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const stripe = device.stripe_linked_subscription;

    const fetchOrganizations = useMemo(
        () =>
            debounce((searchTerm: string) => {
                adminGetAllOrganizations(searchTerm, 500)
                    .then((response) =>
                        setOrganizations(
                            response.data.data.filter(
                                (o) => !o.name.includes("'s Personal Organization"),
                            ),
                        ),
                    )
                    .catch(() => setOrganizations([]));
            }, 300),
        [],
    );

    // Scope the athlete search to the pod's organization (like the Pods page) so it
    // returns the org's individuals instead of every individual system-wide.
    const fetchIndividuals = useMemo(
        () =>
            debounce((searchTerm: string, orgUuid?: string) => {
                adminGetIndividuals({
                    'filter[name]': searchTerm,
                    'filter[has_managing_user]': 1,
                    ...(orgUuid ? { 'filter[organization]': orgUuid } : {}),
                    limit: 100,
                })
                    .then((response) => setIndividuals(response.data.data))
                    .catch(() => setIndividuals([]));
            }, 300),
        [],
    );

    // De-dupe by managing user: one person can have several athlete profiles
    // (individuals) in the same org. Those share a managing_user_uuid and assigning
    // any of them targets the same user, so they collapse to one option (otherwise
    // the dropdown shows multiple identical "name (same uuid)" lines).
    const individualOptions = useMemo(() => {
        const keyFor = (i: Individual) => i.managing_user_uuid ?? i.uuid;
        const byUser = new Map<string, Individual>();
        individuals.forEach((i) => {
            if (!byUser.has(keyFor(i))) {
                byUser.set(keyFor(i), i);
            }
        });
        if (selectedIndividual && !byUser.has(keyFor(selectedIndividual))) {
            byUser.set(keyFor(selectedIndividual), selectedIndividual);
        }
        return Array.from(byUser.values());
    }, [individuals, selectedIndividual]);

    useEffect(() => {
        return () => {
            fetchOrganizations.cancel();
            fetchIndividuals.cancel();
        };
    }, [fetchOrganizations, fetchIndividuals]);

    const openOrgDialog = () => {
        setError(null);
        setSelectedOrg(null);
        setOrgDialogOpen(true);
        fetchOrganizations('');
    };

    const openAssignDialog = () => {
        setError(null);
        setSelectedIndividual(null);
        setIsTransfer(false);
        setAssignDialogOpen(true);
        fetchIndividuals('', device.assigned_org?.uuid);
    };

    const handleChangeOrg = async () => {
        if (!podUuid) return;
        setBusy(true);
        setError(null);
        try {
            await changePodOrganization(podUuid, selectedOrg?.uuid ?? null);
            const orgNote = selectedOrg
                ? `Organization changed to ${selectedOrg.name}`
                : `Organization cleared (was ${device.assigned_org?.name ?? 'unknown'})`;
            void addFp1DeviceNote(device.uuid, `Admin action — ${orgNote}.`);
            setOrgDialogOpen(false);
            onChanged();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Failed to change organization.';
            setError(typeof msg === 'string' ? msg : 'Failed to change organization.');
        } finally {
            setBusy(false);
        }
    };

    const handleAssign = async () => {
        if (!podUuid || !selectedIndividual?.uuid) return;
        setBusy(true);
        setError(null);
        try {
            const individualLabel = `${selectedIndividual.name?.trim() || 'Individual'} (${
                selectedIndividual.managing_user_uuid || selectedIndividual.uuid
            })`;
            if (isTransfer) {
                await transferPodToIndividual(podUuid, selectedIndividual.uuid);
                void addFp1DeviceNote(
                    device.uuid,
                    `Admin action — Pod transferred to ${individualLabel}.`,
                );
            } else {
                await assignPodToIndividual(podUuid, selectedIndividual.uuid);
                void addFp1DeviceNote(
                    device.uuid,
                    `Admin action — Individual assigned: ${individualLabel}.`,
                );
            }
            setAssignDialogOpen(false);
            onChanged();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Failed to assign individual.';
            setError(typeof msg === 'string' ? msg : 'Failed to assign individual.');
        } finally {
            setBusy(false);
        }
    };

    const handleDetach = async () => {
        if (!podUuid) return;
        setBusy(true);
        setError(null);
        try {
            const previousLabel =
                device.assigned_athlete?.name?.trim() ||
                device.assigned_athlete?.email?.trim() ||
                device.assigned_athlete?.uuid ||
                'unknown';
            await detachPodIndividual(podUuid);
            void addFp1DeviceNote(
                device.uuid,
                `Admin action — Individual detached (was ${previousLabel}).`,
            );
            onChanged();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Failed to detach individual.';
            setError(typeof msg === 'string' ? msg : 'Failed to detach individual.');
        } finally {
            setBusy(false);
        }
    };

    const assignedAthlete = device.assigned_athlete;
    const subActive = stripe?.subscription_status?.trim().toLowerCase() === 'active';

    if (!podUuid) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                No hierarchy pod is linked to this serial yet, so pod custody and Stripe links are
                unavailable.
            </Alert>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Hierarchy pod (read-only + admin actions)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Uses existing admin pod endpoints. Assignment targets an <strong>individual</strong>{' '}
                record, not only a user account.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
                {device.custody_status ? (
                    <Chip
                        label={formatStatus(device.custody_status)}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                ) : (
                    <Chip label="Pod custody unknown" size="small" variant="outlined" />
                )}
                {stripe?.subscription_status != null && stripe.subscription_status !== '' && (
                    <Chip
                        label={`Stripe: ${stripe.subscription_status}`}
                        size="small"
                        color={stripeChipColor(stripe.subscription_status)}
                        variant="filled"
                    />
                )}
            </Box>

            {device.pending_transfer_to?.uuid ? (
                <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Pending transfer → {device.pending_transfer_to.name?.trim() || 'Unknown'}
                </Typography>
            ) : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 1.5,
                    mb: 1.5,
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Assigned organization
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                    >
                        {device.assigned_org?.name?.trim() || '—'}
                        {device.assigned_org ? (
                            <StripeLink
                                id={device.assigned_org.stripe_customer_id ?? null}
                                entity="customers"
                            />
                        ) : null}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Assigned athlete (user)
                    </Typography>
                    <Typography variant="body2">
                        {assignedAthlete?.name?.trim() || assignedAthlete?.email?.trim() || '—'}
                    </Typography>
                    {assignedAthlete?.uuid ? (
                        <>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', fontFamily: 'monospace' }}
                            >
                                UUID: {assignedAthlete.uuid}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                            >
                                Subscription active: {subActive ? 'yes' : 'no'}
                            </Typography>
                        </>
                    ) : null}
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Stripe-linked subscription
                    </Typography>
                    <Typography variant="body2">
                        {stripe?.subscription_name?.trim() || stripe?.subscription_id || '—'}
                    </Typography>
                    {stripe?.subscription_id ? (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            <a
                                href={`https://dashboard.stripe.com/subscriptions/${stripe.subscription_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                View in Stripe →
                            </a>
                        </Typography>
                    ) : null}
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Shipped / returned / pod decommissioned
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {device.last_shipped_at || '—'} / {device.last_returned_at || '—'} /{' '}
                        {device.hierarchy_pod_decommissioned_at || '—'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={openOrgDialog} disabled={busy}>
                    Change organization
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={openAssignDialog}
                    disabled={busy || !!assignedAthlete?.uuid}
                >
                    Assign athlete (individual)
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleDetach}
                    disabled={busy || !assignedAthlete?.uuid}
                >
                    Detach athlete
                </Button>
            </Box>

            <Dialog
                open={orgDialogOpen}
                onClose={() => !busy && setOrgDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    Change pod organization
                    <IconButton
                        size="small"
                        onClick={() => setOrgDialogOpen(false)}
                        disabled={busy}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        value={selectedOrg}
                        onChange={(_, v) => setSelectedOrg(v)}
                        onInputChange={(_, val, reason) => {
                            if (reason !== 'reset') fetchOrganizations(val);
                        }}
                        options={organizations}
                        getOptionLabel={(o) => o.name}
                        filterOptions={(o) => o}
                        renderInput={(params) => (
                            <TextField {...params} label="Organization" placeholder="Search…" />
                        )}
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                    >
                        Leave empty and save to clear the pod organization (when permitted by the
                        server).
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOrgDialogOpen(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleChangeOrg} disabled={busy}>
                        {busy ? <CircularProgress size={22} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={assignDialogOpen}
                onClose={() => !busy && setAssignDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    Assign individual to pod
                    <IconButton
                        size="small"
                        onClick={() => setAssignDialogOpen(false)}
                        disabled={busy}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        value={selectedIndividual}
                        onChange={(_, v) => setSelectedIndividual(v)}
                        onInputChange={(_, val, reason) => {
                            if (reason === 'input') {
                                fetchIndividuals(val, device.assigned_org?.uuid);
                            }
                        }}
                        options={individualOptions}
                        getOptionKey={(ind) => ind.managing_user_uuid ?? ind.uuid}
                        isOptionEqualToValue={(a, b) =>
                            (a.managing_user_uuid ?? a.uuid) === (b.managing_user_uuid ?? b.uuid)
                        }
                        getOptionLabel={(ind) =>
                            `${ind.name?.trim() || 'Individual'} (${
                                ind.managing_user_uuid || ind.uuid
                            })`
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Individual"
                                placeholder="Search by name…"
                            />
                        )}
                    />
                    <FormControlLabel
                        sx={{ mt: 2 }}
                        control={
                            <Switch
                                checked={isTransfer}
                                onChange={(e) => setIsTransfer(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">
                                    {isTransfer
                                        ? 'Transfer (athlete has active sub)'
                                        : 'Assignment (no active sub)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Toggle on if this athlete has an active combo subscription in
                                    Stripe — uses the transfer endpoint instead of assign.
                                </Typography>
                            </Box>
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={busy || !selectedIndividual?.uuid}
                    >
                        {busy ? <CircularProgress size={22} /> : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default Fp1HierarchyPodAdminPanel;
